import { Form, Formik } from "formik";
import * as Yup from "yup";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import AutocompleteFilter, { OptionType } from "@components/Forms/FormInputs/AutocompleteFilter";
import TextField from "@components/Forms/FormInputs/TextField";

import { usePortfolioQuery } from "@queries/portfolio.query";

import useSmartSearchStore from "@store/smartSearch";

import theme from "@theme/index";

interface IAddToPortfolioProperties {
  addToTechnology: (technologyId: string, technologyName: string) => Promise<void>;
  createTechnology: (technologyName: string, technologyDescription?: string) => Promise<void>;
}

export default function AddToPortfolio({ addToTechnology, createTechnology }: IAddToPortfolioProperties) {
  const { data: portfolios, status } = usePortfolioQuery();
  const dividerStyling = { borderColor: theme.palette.secondary.light, height: "1px", width: "40%" };

  const { isSmartSearchEnabled, smartSearchDescription } = useSmartSearchStore();

  const existingNames: string[] = [];
  let existingPortfolios;
  if (portfolios) {
    if ("portfolio" in portfolios && Array.isArray(portfolios.portfolio)) {
      existingPortfolios = portfolios.portfolio;
    } else if (Array.isArray(portfolios)) {
      existingPortfolios = portfolios;
    } else {
      throw new TypeError("Invalid portfolios structure");
    }
  }

  const addToTechnologySchema = Yup.object().shape({
    option: Yup.object()
      .shape({ id: Yup.string(), label: Yup.string() })
      .typeError("Select an existing Technology")
      .required("Required"),
  });

  const createTechnologySchema = Yup.object().shape({
    name: Yup.string().required("Required").notOneOf(existingNames, "Technology with this name already exists."),
  });

  let portfolioOptions: OptionType[] = [];
  if (existingPortfolios) {
    portfolioOptions = existingPortfolios
      .sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime())
      .map((technology) => ({ id: technology.id, label: technology.name }));
  }

  return (
    <Box
      sx={{
        p: 1,
        width: "32rem",
      }}
    >
      <Formik
        initialValues={{
          option: { id: "", label: "" },
        }}
        onSubmit={async (values) => {
          await addToTechnology(values.option.id, values.option.label);
        }}
        validationSchema={addToTechnologySchema}
      >
        {({ dirty, isSubmitting, isValid, values }) => (
          <Box
            component={Form}
            display="flex"
            flexDirection="column"
            sx={{
              gap: 1,
              width: "100%", // Fix IE 11 issue.
            }}
          >
            <AutocompleteFilter
              color="secondary"
              isDisablePortal={false}
              isFullWidth
              isLoading={status !== "success"}
              label="Add to an Existing Technology"
              name="option"
              options={portfolioOptions}
              type="text"
              value={values.option}
            />

            <Button
              disabled={!isValid || !dirty || isSubmitting}
              fullWidth
              size="medium"
              type="submit"
              variant="contained"
            >
              {isSubmitting ? <CircularProgress size={20} /> : "Add To Technology"}
            </Button>
          </Box>
        )}
      </Formik>
      <Box alignItems="center" display="flex" justifyContent="center" sx={{ my: 3 }}>
        <Divider sx={dividerStyling} />
        <Typography color="secondary" sx={{ mx: 2 }} variant="h6">
          OR
        </Typography>
        <Divider sx={dividerStyling} />
      </Box>
      <Formik
        initialValues={{
          name: "",
        }}
        onSubmit={async (values) => {
          await createTechnology(values.name, isSmartSearchEnabled ? smartSearchDescription : "");
        }}
        validationSchema={createTechnologySchema}
      >
        {({ dirty, isSubmitting, isValid }) => (
          <Box
            component={Form}
            display="flex"
            flexDirection="column"
            sx={{
              gap: 1,
              width: "100%", // Fix IE 11 issue.
            }}
          >
            <TextField
              color="secondary"
              inputProperties={{ maxLength: "90", minLength: "1" }}
              isFullWidth
              isRequired
              label="Enter a New Technology Name"
              name="name"
              size="medium"
              type="text"
            />
            <Button
              disabled={!isValid || !dirty || isSubmitting || status !== "success"}
              fullWidth
              size="medium"
              type="submit"
              variant="contained"
            >
              {isSubmitting ? <CircularProgress size={20} /> : "Create Technology"}
            </Button>
          </Box>
        )}
      </Formik>
    </Box>
  );
}
